import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import ImageLight from "../assets/img/forgot-password-office.jpeg";
import ImageDark from "../assets/img/forgot-password-office-dark.jpeg";
import { Label, Input, Button, HelperText } from "@windmill/react-ui";
import adminApi from "../apis/admin/adminApi";

function ForgotPasswordOTP() {
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Verify OTP, 3: Reset Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const history = useHistory();

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();

    if (!email) {
      alert("Please enter your email");
      return;
    }

    setIsLoading(true);
    try {
      const response = await adminApi.requestPasswordResetOTP(email);

      if (response.success) {
        alert(response.message || "OTP sent to your email");
        setStep(2);
        setTimer(60); // 60 seconds timer
        setCanResend(false);
      } else {
        alert(response.message || "Failed to send OTP");
      }
    } catch (error) {
      alert(error.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      alert("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      // Optional: Validate OTP first
      const validationResponse = await adminApi.validatePasswordResetOTP({
        email,
        otp,
      });

      if (validationResponse.success) {
        setStep(3);
        alert("OTP verified. Now set your new password.");
      } else {
        alert(validationResponse.message || "Invalid OTP");
      }
    } catch (error) {
      alert(error.message || "Failed to verify OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);
    try {
      const response = await adminApi.resendPasswordResetOTP(email);

      if (response.success) {
        alert(response.message || "New OTP sent to your email");
        setTimer(60);
        setCanResend(false);
      } else {
        alert(response.message || "Failed to resend OTP");
      }
    } catch (error) {
      alert(error.message || "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!newPassword || !confirmPassword) {
      alert("Please fill in all fields");
      return;
    }

    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Passwords don't match");
      return;
    }

    setIsLoading(true);
    try {
      const response = await adminApi.verifyOTPAndResetPassword({
        email,
        otp,
        newPassword,
      });

      if (response.success) {
        alert("Password reset successfully!");
        // Store token if provided (auto-login)
        if (response.data?.token) {
          localStorage.setItem("adminToken", response.data.token);
          // Optionally auto-login
          history.push("/app/dashboard");
        } else {
          history.push("/login");
        }
      } else {
        alert(response.message || "Failed to reset password");
      }
    } catch (error) {
      alert(error.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Request OTP
  const renderStep1 = () => (
    <>
      <h1 className="mb-4 text-xl font-semibold text-gray-700 dark:text-gray-200">
        Reset Password
      </h1>
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        Enter your email address and we'll send you an OTP to reset your
        password.
      </p>

      <form onSubmit={handleRequestOTP}>
        <Label>
          <span>Email</span>
          <Input
            className="mt-1"
            type="email"
            placeholder="admin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Label>

        <Button block className="mt-6" type="submit" disabled={isLoading}>
          {isLoading ? "Sending OTP..." : "Send OTP"}
        </Button>
      </form>
    </>
  );

  // Step 2: Verify OTP
  const renderStep2 = () => (
    <>
      <h1 className="mb-4 text-xl font-semibold text-gray-700 dark:text-gray-200">
        Enter OTP
      </h1>
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        We've sent a 6-digit OTP to <strong>{email}</strong>. Enter it below.
      </p>

      <form onSubmit={handleVerifyOTP}>
        <Label>
          <span>6-digit OTP</span>
          <Input
            className="mt-1 text-center text-2xl font-mono tracking-widest"
            type="text"
            placeholder="123456"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setOtp(value);
            }}
            maxLength="6"
            required
          />
          <HelperText className="text-center">
            Enter the 6-digit code from your email
          </HelperText>
        </Label>

        <div className="mt-4 text-center">
          {timer > 0 ? (
            <p className="text-sm text-gray-500">
              Resend OTP in {timer} seconds
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={!canResend || isLoading}
              className="text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Resending..." : "Didn't receive OTP? Resend"}
            </button>
          )}
        </div>

        <div className="flex gap-4 mt-6">
          <Button
            layout="outline"
            block
            onClick={() => {
              setStep(1);
              setOtp("");
            }}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button block type="submit" disabled={isLoading || otp.length !== 6}>
            {isLoading ? "Verifying..." : "Verify OTP"}
          </Button>
        </div>
      </form>
    </>
  );

  // Step 3: Reset Password
  const renderStep3 = () => (
    <>
      <h1 className="mb-4 text-xl font-semibold text-gray-700 dark:text-gray-200">
        Set New Password
      </h1>
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        Enter your new password below.
      </p>

      <form onSubmit={handleResetPassword}>
        <Label className="mt-4">
          <span>New Password (min 8 characters)</span>
          <Input
            className="mt-1"
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength="8"
          />
        </Label>

        <Label className="mt-4">
          <span>Confirm New Password</span>
          <Input
            className="mt-1"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength="8"
          />
        </Label>

        <div className="flex gap-4 mt-6">
          <Button
            layout="outline"
            block
            onClick={() => {
              setStep(2);
              setNewPassword("");
              setConfirmPassword("");
            }}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            block
            type="submit"
            disabled={
              isLoading ||
              newPassword.length < 8 ||
              newPassword !== confirmPassword
            }
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      </form>
    </>
  );

  // Progress indicator
  const renderProgress = () => (
    <div className="mb-6">
      <div className="flex items-center justify-center">
        {[1, 2, 3].map((stepNumber) => (
          <div key={stepNumber} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= stepNumber
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-500 dark:bg-gray-700"
              }`}
            >
              {stepNumber}
            </div>
            {stepNumber < 3 && (
              <div
                className={`w-16 h-1 ${
                  step > stepNumber
                    ? "bg-purple-600"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>Request OTP</span>
        <span>Verify OTP</span>
        <span>New Password</span>
      </div>
    </div>
  );

  return (
    <div className="flex items-center min-h-screen p-6 bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 h-full max-w-4xl mx-auto overflow-hidden bg-white rounded-lg shadow-xl dark:bg-gray-800">
        <div className="flex flex-col overflow-y-auto md:flex-row">
          <div className="h-32 md:h-auto md:w-1/2">
            <img
              aria-hidden="true"
              className="object-cover w-full h-full dark:hidden"
              src={ImageLight}
              alt="Office"
            />
            <img
              aria-hidden="true"
              className="hidden object-cover w-full h-full dark:block"
              src={ImageDark}
              alt="Office"
            />
          </div>
          <main className="flex items-center justify-center p-6 sm:p-12 md:w-1/2">
            <div className="w-full">
              {renderProgress()}

              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}

              <p className="mt-8 text-center">
                <Link
                  className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:underline"
                  to="/login"
                >
                  Back to login
                </Link>
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordOTP;
